/**
 * FeedbackDetails - Organism component for displaying detailed feedback view
 */
import React, { useState } from 'react';
import FeedbackStatus, { FeedbackStatusType } from '../atoms/FeedbackStatus';
import UserAvatar from '../atoms/UserAvatar';
import FeedbackMetadata from '../molecules/FeedbackMetadata';
import { FeedbackPriorityType } from '../molecules/FeedbackCard';
import { formatDistanceToNow, format } from 'date-fns';
import { getDisplayName, getUserTooltip } from '../../utils/userDisplay';

interface UserSummary {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AppSummary {
  client_id: string;
  name: string;
  description?: string;
  logo_uri?: string;
}

interface CommentSummary {
  id: number;
  content: string;
  user_id: string;
  created_at: string;
  author?: {
    auth0_user_id: string;
    name: string;
    email: string;
    picture: string;
  };
}

interface SentimentData {
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  themes: string[];
  summary: string;
  recommendations: string[];
  analyzed_at: string;
  model_version: string;
  context_analysis?: string;
}

interface FeedbackData {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatusType;
  priority: FeedbackPriorityType;
  created_at: string;
  updated_at?: string;
  resolved_at?: string;
  user?: UserSummary;
  app?: AppSummary;
  assigned_to?: string;
  assigned_user?: UserSummary;
  resolved_by?: string;
  resolver?: UserSummary;
  external_id?: string;
  source_url?: string;
  extra_data?: any;
  comments?: CommentSummary[];
  sentiment_analysis?: SentimentData;
}

interface FeedbackDetailsProps {
  feedback: FeedbackData;
  loading?: boolean;
  error?: string;
  onStatusChange?: (id: number, status: FeedbackStatusType) => void;
  onPriorityChange?: (id: number, priority: FeedbackPriorityType) => void;
  onAddComment?: (id: number, comment: string) => Promise<void> | void;
  onClose?: () => void;
  className?: string;
}

const priorityConfig = {
  low: { color: 'text-gray-600', icon: '🔹', bgColor: 'bg-gray-100' },
  medium: { color: 'text-blue-600', icon: '🔶', bgColor: 'bg-blue-100' },
  high: { color: 'text-orange-600', icon: '🔥', bgColor: 'bg-orange-100' },
  urgent: { color: 'text-red-600', icon: '🚨', bgColor: 'bg-red-100' }
};

const sentimentConfig = {
  positive: { 
    color: 'text-green-700 dark:text-green-400', 
    icon: '😊', 
    bgColor: 'bg-green-100 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700'
  },
  negative: { 
    color: 'text-red-700 dark:text-red-400', 
    icon: '😔', 
    bgColor: 'bg-red-100 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-700'
  },
  neutral: { 
    color: 'text-gray-700 dark:text-gray-400', 
    icon: '😐', 
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-600'
  }
};


const SentimentAnalysis: React.FC<{ sentiment: SentimentData }> = ({ sentiment }) => {
  const config = sentimentConfig[sentiment.sentiment];
  const confidencePercent = Math.round(sentiment.confidence * 100);
  
  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
        <span className="mr-2">🤖</span>
        AI Sentiment Analysis
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
          ({confidencePercent}% confidence)
        </span>
      </h3>
      
      <div className="space-y-4">
        {/* Sentiment Badge with Summary */}
        <div className={`flex items-start space-x-3 p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}>
          <span className="text-lg mt-0.5">{config.icon}</span>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`text-sm font-semibold ${config.color}`}>
                {sentiment.sentiment.charAt(0).toUpperCase() + sentiment.sentiment.slice(1)} Sentiment
              </span>
            </div>
            <p className={`text-sm ${config.color} opacity-90`}>
              {sentiment.summary}
            </p>
          </div>
        </div>

        {/* Context Analysis - The key missing piece! */}
        {sentiment.context_analysis && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              <span className="mr-1">💭</span>
              Context Analysis
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {sentiment.context_analysis}
            </p>
          </div>
        )}
        
        {/* Key Themes */}
        {sentiment.themes && sentiment.themes.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              <span className="mr-1">🏷️</span>
              Key Themes ({sentiment.themes.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {sentiment.themes.map((theme, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs rounded-full border border-blue-200 dark:border-blue-700"
                >
                  {theme}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Actionable Recommendations */}
        {sentiment.recommendations && sentiment.recommendations.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              <span className="mr-1">💡</span>
              Actionable Recommendations ({sentiment.recommendations.length})
            </h4>
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-700/50 p-3">
              <ul className="space-y-2">
                {sentiment.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2 mt-0.5 font-bold">
                      {index + 1}.
                    </span>
                    <span className="flex-1">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* Analysis Metadata */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              Analyzed {formatDistanceToNow(new Date(sentiment.analyzed_at), { addSuffix: true })}
            </span>
            <span className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">
              {sentiment.model_version}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedbackDetails: React.FC<FeedbackDetailsProps> = ({
  feedback,
  loading = false,
  error,
  onStatusChange,
  onPriorityChange,
  onAddComment,
  onClose,
  className = ''
}) => {
  const [showMetadata, setShowMetadata] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Debug: Log comments when feedback changes
  React.useEffect(() => {
    console.log('FeedbackDetails - Feedback updated:', {
      id: feedback.id,
      commentsCount: feedback.comments?.length || 0,
      comments: feedback.comments
    });
  }, [feedback.id, feedback.comments]);


  const priorityConfig_item = priorityConfig[feedback.priority];

  const handleStatusChange = (newStatus: FeedbackStatusType) => {
    onStatusChange?.(feedback.id, newStatus);
  };

  const handlePriorityChange = (newPriority: FeedbackPriorityType) => {
    onPriorityChange?.(feedback.id, newPriority);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setIsAddingComment(true);
    try {
      await onAddComment?.(feedback.id, newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsAddingComment(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      absolute: format(date, 'PPpp'),
      relative: formatDistanceToNow(date, { addSuffix: true })
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading feedback details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <div className="text-red-600 dark:text-red-400 font-medium mb-2">
          Error loading feedback details
        </div>
        <div className="text-red-500 dark:text-red-300 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 relative ${className}`} data-testid="feedback-details-container" data-element="feedback-details">
      {/* Close Button - Positioned absolutely in top right corner */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 z-10"
          data-testid="feedback-details-close-button"
          data-element="close-button"
        >
          ✕
        </button>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 pr-16" data-testid="feedback-details-header" data-element="header">
        <div className="flex items-center space-x-4" data-testid="feedback-details-title-section" data-element="title-section">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-testid="feedback-details-title" data-element="title">
            Feedback #{feedback.id}
          </h1>
          <div data-testid="feedback-details-status" data-element="status-badge">
            <FeedbackStatus status={feedback.status} />
          </div>
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${priorityConfig_item.bgColor}`} data-testid="feedback-details-priority" data-element="priority-badge">
            <span className={`text-sm ${priorityConfig_item.color}`}>
              {priorityConfig_item.icon}
            </span>
            <span className={`text-sm font-medium ${priorityConfig_item.color}`}>
              {feedback.priority}
            </span>
          </div>
          
          {/* Sentiment Badge in Header */}
          {feedback.sentiment_analysis && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${sentimentConfig[feedback.sentiment_analysis.sentiment].bgColor} ${sentimentConfig[feedback.sentiment_analysis.sentiment].borderColor} border`} data-testid="feedback-details-sentiment-badge" data-element="sentiment-badge">
              <span className="text-sm">
                {sentimentConfig[feedback.sentiment_analysis.sentiment].icon}
              </span>
              <span className={`text-sm font-medium ${sentimentConfig[feedback.sentiment_analysis.sentiment].color}`}>
                {feedback.sentiment_analysis.sentiment}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2" data-testid="feedback-details-actions" data-element="header-actions">
          {/* Empty space where toggle button was */}
        </div>
      </div>

      {/* Toggle Metadata Button - Positioned above body */}
      <div className="flex justify-end px-6 py-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowMetadata(!showMetadata)}
          className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          data-testid="feedback-details-toggle-metadata"
          data-element="toggle-metadata-button"
        >
          {showMetadata ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      <div className="flex" data-testid="feedback-details-body" data-element="body">
        {/* Main Content */}
        <div className="flex-1 p-6" data-testid="feedback-details-main-content" data-element="main-content">
          {/* User Feedback Container */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6" data-testid="feedback-details-user-feedback" data-element="user-feedback">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center" data-testid="feedback-details-user-feedback-title" data-element="user-feedback-title">
              <span className="mr-2">💬</span>
              User Feedback
            </h3>
            
            <div className="space-y-3">
              {/* Feedback Title */}
              <div data-testid="feedback-details-feedback-title-section" data-element="feedback-title-section">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100" data-testid="feedback-details-main-title" data-element="main-title">
                  {feedback.title}
                </h2>
              </div>

              {/* Feedback Description */}
              <div data-testid="feedback-details-feedback-description-section" data-element="feedback-description-section">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</h4>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-3" data-testid="feedback-details-description" data-element="description">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {feedback.description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sentiment Analysis */}
          {feedback.sentiment_analysis && (
            <div data-testid="feedback-details-sentiment-analysis" data-element="sentiment-analysis">
              <SentimentAnalysis sentiment={feedback.sentiment_analysis} />
            </div>
          )}

          {/* User Information */}
          <div className="flex items-center space-x-3 mb-6" data-testid="feedback-details-user-info" data-element="user-info">
            <div data-testid="feedback-details-user-avatar" data-element="user-avatar">
              <UserAvatar
                name={feedback.user?.name}
                email={feedback.user?.email}
                picture={feedback.user?.picture}
                size="lg"
              />
            </div>
            <div data-testid="feedback-details-user-details" data-element="user-details">
              <div className="font-medium text-gray-900 dark:text-gray-100" data-testid="feedback-details-user-name" data-element="user-name">
                {feedback.user?.name || feedback.user?.email || 'Anonymous User'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400" data-testid="feedback-details-created-at" data-element="created-at">
                {formatDate(feedback.created_at).relative}
              </div>
              {feedback.user?.email && (
                <div className="text-sm text-gray-500 dark:text-gray-400" data-testid="feedback-details-user-email" data-element="user-email">
                  {feedback.user.email}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-4 mb-6" data-testid="feedback-details-actions-section" data-element="actions-section">
            <div className="flex items-center space-x-2" data-testid="feedback-details-status-control" data-element="status-control">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-testid="feedback-details-status-label" data-element="status-label">
                Status:
              </label>
              <select
                value={feedback.status}
                onChange={(e) => handleStatusChange(e.target.value as FeedbackStatusType)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                data-testid="feedback-details-status-select"
                data-element="status-select"
              >
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="flex items-center space-x-2" data-testid="feedback-details-priority-control" data-element="priority-control">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-testid="feedback-details-priority-label" data-element="priority-label">
                Priority:
              </label>
              <select
                value={feedback.priority}
                onChange={(e) => handlePriorityChange(e.target.value as FeedbackPriorityType)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                data-testid="feedback-details-priority-select"
                data-element="priority-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Comments Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6" data-testid="feedback-details-comments-section" data-element="comments-section">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4" data-testid="feedback-details-comments-title" data-element="comments-title">
              Comments ({feedback.comments?.length || 0})
            </h3>
            
            {/* Add Comment */}
            <div className="mb-4" data-testid="feedback-details-add-comment" data-element="add-comment">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm resize-none"
                rows={3}
                data-testid="feedback-details-comment-textarea"
                data-element="comment-textarea"
              />
              <div className="flex justify-end mt-2" data-testid="feedback-details-comment-actions" data-element="comment-actions">
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isAddingComment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  data-testid="feedback-details-add-comment-button"
                  data-element="add-comment-button"
                  title={!newComment.trim() ? 'Please enter a comment to enable this button' : ''}
                >
                  {isAddingComment ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
              
            </div>

            {/* Comments Timeline */}
            <div className="space-y-3" data-testid="feedback-details-comments-timeline" data-element="comments-timeline">
              {feedback.comments && feedback.comments.length > 0 ? (
                feedback.comments.map((comment, index) => (
                  <div key={comment.id} className="relative" data-testid={`feedback-comment-${comment.id}`} data-element="comment-item">
                    {/* Timeline connector */}
                    {feedback.comments && index < feedback.comments.length - 1 && (
                      <div className="absolute left-4 top-10 w-0.5 h-6 bg-gray-200 dark:bg-gray-700" data-element="timeline-connector"></div>
                    )}
                    
                    {/* Comment card */}
                    <div className="flex space-x-3">
                      <div className="flex-shrink-0" data-testid={`feedback-comment-avatar-${comment.id}`} data-element="comment-avatar">
                        <UserAvatar
                          name={getDisplayName(comment.author, comment.user_id)}
                          email={comment.author?.email}
                          picture={comment.author?.picture}
                          size="sm"
                        />
                      </div>
                      <div className="flex-1 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span 
                              className="font-medium text-gray-900 dark:text-gray-100 text-sm cursor-help" 
                              data-testid={`feedback-comment-author-${comment.id}`} 
                              data-element="comment-author"
                              title={getUserTooltip(comment.author, comment.user_id)}
                            >
                              {getDisplayName(comment.author, comment.user_id)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400" data-testid={`feedback-comment-time-${comment.id}`} data-element="comment-time">
                              {formatDate(comment.created_at).relative}
                            </span>
                          </div>
                          {index === 0 && (
                            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full" data-element="latest-badge">
                              Latest
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm" data-testid={`feedback-comment-content-${comment.id}`} data-element="comment-content">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-testid="feedback-details-no-comments" data-element="no-comments">
                  <div className="text-4xl mb-2">💬</div>
                  <p className="text-sm">No comments yet</p>
                  <p className="text-xs mt-1">Be the first to add a comment!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metadata Sidebar */}
        {showMetadata && (
          <div className="w-80 border-l border-gray-200 dark:border-gray-700 p-6">
            <FeedbackMetadata feedback={feedback} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackDetails; 